import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { conversationApi, memberSummaryApi } from '@formosa/shared/api/im';
import imWebSocketClient from '@formosa/shared/api/imWebSocketClient';
import { mapApiErrorToUiModel, resolveUiErrorCopy } from '@formosa/shared/i18n/mapApiErrorToUiModel';
import { resolveMemberMediaUrl } from '@formosa/shared/utils/mediaUrl';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import { useAuth } from '../../auth/AuthContext';
import SafeImage from '../../components/SafeImage';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui';
import {
  CHAT_READ_CONFIRMED,
  applyConfirmedRead,
  applyMessageEditedToInbox,
  applyMessageRevokedToInbox,
  applyRealtimeToInbox,
  conversationTitle,
  inboxConversations,
  indexSummaries,
  summaryIdsFromConversations,
} from './travelChatModel';
import './travel-messages.css';
import useConversationTitles from './useConversationTitles';

export default function TravelInboxPage({ variant = 'page' } = {}) {
  const { t, tf, intlLocale } = useTravelTranslation();
  const { user } = useAuth();
  const myMemberId = user?.memberId || user?.id;
  const generation = useRef(0);
  const conversationsRef = useRef([]);
  const [phase, setPhase] = useState('loading');
  const [errorModel, setErrorModel] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [summaries, setSummaries] = useState({});
  const confirmedReads = useRef({});
  const groupTitles = useConversationTitles(conversations, myMemberId);

  useEffect(() => { confirmedReads.current = {}; }, [myMemberId]);
  useEffect(() => {
    const confirmed = receipt => {
      if (String(receipt?.memberId) !== String(myMemberId)) return;
      const previous = confirmedReads.current[receipt.conversationId];
      if (previous && previous.lastReadSeq > receipt.lastReadSeq) return;
      confirmedReads.current[receipt.conversationId] = receipt;
      setConversations(rows => applyConfirmedRead(rows, receipt, myMemberId));
    };
    const local = event => confirmed(event.detail);
    window.addEventListener(CHAT_READ_CONFIRMED, local);
    const off = imWebSocketClient.subscribe(event => { if (event?.type === 'READ_UPDATED') confirmed(event.data); });
    return () => { window.removeEventListener(CHAT_READ_CONFIRMED, local); off?.(); };
  }, [myMemberId]);

  const load = useCallback(async () => {
    const requestGeneration = ++generation.current;
    setPhase('loading');
    setErrorModel(null);
    try {
      let list = inboxConversations(await conversationApi.listMine());
      Object.values(confirmedReads.current).forEach(receipt => { list = applyConfirmedRead(list, receipt, myMemberId); });
      if (requestGeneration !== generation.current) return;
      conversationsRef.current = list;
      setConversations(list);
      setPhase('ready');
      const ids = summaryIdsFromConversations(list);
      if (!ids.length) return;
      try {
        const rows = await memberSummaryApi.batch(ids);
        if (requestGeneration !== generation.current) return;
        setSummaries(indexSummaries(rows));
      } catch {
        /* identity fallback below */
      }
    } catch (cause) {
      if (requestGeneration !== generation.current) return;
      setErrorModel(mapApiErrorToUiModel(cause, { t }));
      setPhase('error');
    }
  }, [t, myMemberId]);

  useEffect(() => {
    load();
    return () => { generation.current += 1; };
  }, [load]);

  useEffect(() => {
    const off = imWebSocketClient.subscribe((envelope) => {
      const type = envelope?.type;
      if (!envelope?.data) return;
      if (type === 'MESSAGE_CREATED') {
        setConversations((current) => {
          const next = applyRealtimeToInbox(current, envelope.data, myMemberId);
          conversationsRef.current = next;
          return next;
        });
        return;
      }
      if (type === 'MESSAGE_EDITED') {
        setConversations((current) => {
          const next = applyMessageEditedToInbox(current, envelope.data);
          conversationsRef.current = next;
          return next;
        });
        return;
      }
      if (type === 'MESSAGE_REVOKED') {
        setConversations((current) => {
          const next = applyMessageRevokedToInbox(current, envelope.data);
          conversationsRef.current = next;
          return next;
        });
      }
    });
    return () => {
      if (typeof off === 'function') off();
    };
  }, [myMemberId]);

  if (phase === 'loading') {
    return (
      <main className="travel-messages-state">
        <LoadingState label={t('travel.common.loading')} />
      </main>
    );
  }
  if (phase === 'error') {
    const copy = resolveUiErrorCopy(errorModel, t);
    return (
      <main className="travel-messages-state">
        <ErrorState
          title={copy.title}
          description={copy.description}
          retryable={copy.retryable}
          retryLabel={t('travel.common.retry')}
          onRetry={load}
        />
      </main>
    );
  }

  return (
    <main className="travel-inbox">
      <header className="travel-inbox__header">
        {variant === 'rail' ? <h2>{t('travel.chat.message')}</h2> : <h1>{t('travel.chat.message')}</h1>}
        {variant === 'rail' ? null : <p>{t('travel.chat.inboxHint')}</p>}
      </header>
      {conversations.length === 0 ? (
        <EmptyState
          className="travel-messages-state travel-messages-state--empty"
          title={t('travel.chat.inboxEmpty')}
          description={t('travel.chat.inboxEmptyHint')}
        />
      ) : (
        <ul className="travel-inbox__list">
          {conversations.map((conversation) => {
            const title = conversationTitle(conversation, summaries, groupTitles);
            const peer = summaries[conversation.peerMemberId];
            const unread = conversation.unreadCount;
            const avatar = resolveMemberMediaUrl(peer?.avatarUrl || conversation.avatarUrl);
            return (
              <li key={conversation.conversationId}>
                <Link
                  className="travel-inbox__row"
                  to={`/messages/${conversation.conversationId}`}
                  aria-label={unread > 0 ? tf('travel.chat.unreadCount', { name: title, count: unread }) : title}
                >
                  <SafeImage
                    className="travel-inbox__avatar"
                    src={avatar}
                    alt=""
                    fallback={<span className="travel-inbox__avatar" aria-hidden="true">{title.slice(0, 1).toUpperCase()}</span>}
                  />
                  <span className="travel-inbox__body">
                    <strong>{title}</strong>
                    <em>{conversation.lastMessagePreview || t('travel.chat.noMessages')}</em>
                  </span>
                  <span className="travel-inbox__meta">
                    <time dateTime={conversation.lastMessageAt || undefined}>{conversation.lastMessageAt && !Number.isNaN(new Date(conversation.lastMessageAt).getTime()) ? new Intl.DateTimeFormat(intlLocale, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(conversation.lastMessageAt)) : ''}</time>
                    {conversation.muted ? <span>{t('travel.chat.muted')}</span> : null}
                    {unread > 0 ? <span className="travel-inbox__unread">{unread}</span> : null}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </main>
  );
}
